/**
 * ForgotPasswordPage.js - 找回密码页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  message,
} from 'antd';
import {
  MailOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { authApi } from '../../api/index';
import { useStore } from '../../contexts/StoreContext';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { storeName, logoUrl } = useStore();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');

  /**
   * 发送重置密码邮件
   */
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 获取当前域名作为 baseUrl
      const baseUrl = window.location.origin;
      await authApi.sendResetEmail(values.email, baseUrl);
      setEmailSent(true);
      setEmail(values.email);
      message.success('重置邮件已发送，请检查邮箱');
    } catch (err) {
      message.error(err?.response?.data?.message || '发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* 店铺品牌标识 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={storeName}
              style={{ maxHeight: 100, maxWidth: '100%', marginBottom: 8 }}
            />
          ) : (
            <Title
              level={2}
              style={{
                color: COLORS.ink,
                margin: 0,
                letterSpacing: '0.2em',
                fontWeight: 400,
                fontSize: 24,
              }}
            >
              {storeName}
            </Title>
          )}
          <div
            style={{
              width: 40,
              height: 2,
              background: COLORS.cinnabar,
              margin: '12px auto 0',
            }}
          />
        </div>

        <Card
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: 'none',
          }}
          bodyStyle={{ padding: '32px 32px 24px', background: COLORS.paper }}
        >
          {!emailSent ? (
            <>
              <Title
                level={3}
                style={{
                  textAlign: 'center',
                  marginBottom: 12,
                  color: COLORS.ink,
                  fontWeight: 400,
                }}
              >
                找回密码
              </Title>
              <Text
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginBottom: 24,
                  color: COLORS.lightInk,
                }}
              >
                输入您的注册邮箱，我们将发送密码重置链接
              </Text>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                size="large"
              >
                <Form.Item
                  name="email"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>邮箱</span>}
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="请输入注册邮箱"
                    autoComplete="email"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    style={{
                      height: 48,
                      fontSize: 16,
                      borderRadius: 8,
                      background: COLORS.teaBrown,
                      borderColor: COLORS.teaBrown,
                    }}
                  >
                    发送重置邮件
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link to="/login">
                  <Text style={{ color: COLORS.lightInk }}>← 返回登录</Text>
                </Link>
              </div>
            </>
          ) : (
            <>
              <Title
                level={3}
                style={{
                  textAlign: 'center',
                  marginBottom: 12,
                  color: COLORS.ink,
                  fontWeight: 400,
                }}
              >
                检查邮箱
              </Title>
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <SafetyOutlined
                  style={{
                    fontSize: 56,
                    color: COLORS.bambooGreen,
                    marginBottom: 16,
                  }}
                />
                <Text style={{ display: 'block', fontSize: 15, marginBottom: 8, color: COLORS.ink }}>
                  重置邮件已发送至
                </Text>
                <Text
                  strong
                  style={{
                    fontSize: 16,
                    color: COLORS.teaBrown,
                  }}
                >
                  {email}
                </Text>
                <div
                  style={{
                    marginTop: 24,
                    textAlign: 'left',
                    background: `${COLORS.teaBrown}08`,
                    padding: 16,
                    borderRadius: 8,
                    border: `1px solid ${COLORS.lightBorder}`,
                  }}
                >
                  <Text
                    style={{
                      display: 'block',
                      marginBottom: 8,
                      color: COLORS.lightInk,
                      fontSize: 13,
                    }}
                  >
                    请按以下步骤操作：
                  </Text>
                  <ol style={{ margin: 0, paddingLeft: 20, color: COLORS.lightInk }}>
                    <li style={{ marginBottom: 6 }}>登录您的邮箱</li>
                    <li style={{ marginBottom: 6 }}>查找来自本平台的密码重置邮件</li>
                    <li style={{ marginBottom: 6 }}>点击邮件中的"重置密码"按钮</li>
                  </ol>
                </div>
                <Text
                  style={{
                    display: 'block',
                    marginTop: 16,
                    fontSize: 12,
                    color: COLORS.lightInk,
                  }}
                >
                  如果没有收到邮件，请检查垃圾邮件文件夹
                </Text>
              </div>

              <Button
                block
                onClick={() => {
                  setEmailSent(false);
                  form.resetFields();
                }}
                style={{
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  marginTop: 16,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
              >
                重新输入邮箱
              </Button>

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link to="/login">
                  <Text style={{ color: COLORS.lightInk }}>← 返回登录</Text>
                </Link>
              </div>
            </>
          )}
        </Card>

        {/* 返回首页 */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/">
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>← 返回首页</Text>
          </Link>
        </div>
      </div>
    </div>
  );
}
